import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Loading } from '../../components/ui/Loading';
import { ErrorAlert } from '../../components/ui/ErrorAlert';

interface Stats {
  contacts: number;
  pending: number;
  employees: number;
  users: number;
}

export const AdminDashboard = () => {
  const [stats, setStats] = useState<Stats>({ contacts: 0, pending: 0, employees: 0, users: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [contacts, pending, employees, users] = await Promise.all([
        supabase.from('phone_book').select('id', { count: 'exact', head: true }),
        supabase.from('phone_book_requests').select('id', { count: 'exact', head: true }).eq('status', 'UNDER_REVIEW'),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'EMPLOYEE'),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
      ]);
      setStats({
        contacts: contacts.count ?? 0,
        pending: pending.count ?? 0,
        employees: employees.count ?? 0,
        users: users.count ?? 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading) return <Loading full />;
  if (error) return <ErrorAlert message={error} onRetry={loadStats} />;

  const items = [
    { label: 'Total Contacts', value: stats.contacts, color: 'bg-blue-100 text-blue-800' },
    { label: 'Pending Requests', value: stats.pending, color: 'bg-yellow-100 text-yellow-800' },
    { label: 'Employees', value: stats.employees, color: 'bg-green-100 text-green-800' },
    { label: 'Users', value: stats.users, color: 'bg-purple-100 text-purple-800' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-sm text-gray-600">Overview of Tandulwadi Phone Book.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => (
          <div key={item.label} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-600">{item.label}</p>
            <p className={`mt-1 inline-block rounded px-2 py-0.5 text-2xl font-bold ${item.color}`}>{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
